import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, PlusCircle, CheckCircle, FileText, Building2, Users } from 'lucide-react';
import { PageSEO } from '../components/SEO';

/**
 * GmbhGruenden - TasteSkill Premium Redesign
 * 
 * Verbesserungen:
 * - Professionelleres Layout passend zur V3-Final Startseite
 * - Bessere visuelle Hierarchie
 * - Klarere Prozessdarstellung
 * - Einheitliche Designsprache
 */

const GmbhGruenden = () => {
  const benefits = [
    'Haftungsbeschränkung auf das Gesellschaftsvermögen',
    'Hohe Reputation und Vertrauenswürdigkeit',
    'Bessere Finanzierungsmöglichkeiten',
    'Flexiblere Gestaltungsmöglichkeiten',
    'Steuerliche Vorteile bei Gewinnausschüttung',
    'Anonymität der Gesellschafter möglich',
  ];

  const requirements = [
    { title: 'Stammkapital', description: 'Mindestens 25.000 € (12.500 € bei Gründung einzuzahlen)', icon: Building2 },
    { title: 'Gesellschafter', description: 'Mindestens eine natürliche oder juristische Person', icon: Users },
    { title: 'Geschäftsführer', description: 'Mindestens eine natürliche Person mit Wohnsitz in der EU', icon: FileText },
    { title: 'Firmensitz', description: 'Ladungsfähige Geschäftsadresse in Deutschland', icon: Building2 },
  ];

  const steps = [
    { num: '1', title: 'Gesellschaftsvertrag erstellen', description: 'Wir erstellen einen individuellen Gesellschaftsvertrag oder nutzen das Musterprotokoll.' },
    { num: '2', title: 'Notarielle Beurkundung', description: 'Der Gesellschaftsvertrag muss notariell beurkundet werden.' },
    { num: '3', title: 'Geschäftskonto eröffnen', description: 'Eröffnung eines Geschäftskontos und Einzahlung des Stammkapitals.' },
    { num: '4', title: 'Handelsregistereintrag', description: 'Anmeldung beim Handelsregister durch den Notar.' },
    { num: '5', title: 'Gewerbeanmeldung', description: 'Anmeldung beim Gewerbeamt und weiteren Behörden.' },
  ];

  return (
    <>
      <PageSEO 
        title="GmbH gründen – Professionelle Begleitung | Firmbay"
        description="GmbH gründen mit professioneller Begleitung. Firmbay unterstützt Sie von der Beratung bis zur Handelsregistereintragung. Jetzt informieren."
        path="/gmbh-gruenden"
      />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-24 pb-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFA] to-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E9F7F6] rounded-full mb-4">
              <PlusCircle className="w-3.5 h-3.5 text-[#009E9A]" />
              <span className="text-[11px] font-semibold text-[#009E9A] uppercase tracking-wider">
                GmbH-Gründung
              </span>
            </div>
            <h1 className="text-[28px] md:text-[34px] font-bold text-[#0B1930] mb-3 leading-tight tracking-[-0.02em]">
              GmbH gründen – Professionelle Begleitung
            </h1>
            <p className="text-base text-gray-600 mb-6 leading-relaxed">
              Die Gesellschaft mit beschränkter Haftung (GmbH) ist die beliebteste Rechtsform für Unternehmen in Deutschland. 
              Wir begleiten Sie bei jedem Schritt der Gründung – von der Beratung bis zur Eintragung.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/onboarding">
                <Button className="bg-[#0B1930] hover:bg-[#162844] text-white rounded-lg text-sm px-5 py-2.5">
                  Formular starten
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" className="border-gray-300 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] rounded-lg text-sm px-5 py-2.5">
                  Beratung anfragen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6]">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-6">
            Vorteile einer GmbH
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-4">
                <div className="w-5 h-5 rounded-full bg-[#009E9A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
                <p className="text-sm text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-6">
            Voraussetzungen für die GmbH-Gründung
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {requirements.map((req, index) => {
              const Icon = req.icon;
              return (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#009E9A] transition-colors group">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#009E9A] transition-colors">
                      <Icon className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0B1930] mb-1">{req.title}</h3>
                      <p className="text-sm text-gray-600">{req.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F8FAFA] border-y border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-8">
            Der Gründungsprozess
          </h2>
          <div className="relative">
            {/* Vertical line on desktop */}
            <div className="hidden md:block absolute left-[19px] top-6 bottom-6 w-0.5 bg-[#E9F7F6]"></div>
            
            <div className="space-y-5">
              {steps.map((step, index) => (
                <div key={step.num} className="flex items-start gap-4 relative">
                  <div className="w-10 h-10 bg-[#009E9A] text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 relative z-10">
                    {step.num}
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-5 flex-1">
                    <h3 className="font-bold text-[#0B1930] mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="p-5 border border-gray-200 rounded-xl bg-[#F8FAFA]">
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong className="text-[#0B1930]">Hinweis:</strong> Firmbay bietet keine Rechts- oder Steuerberatung an. 
              Die Plattform dient der Vermittlung und organisatorischen Begleitung von GmbH-Gründungen. 
              Sämtliche gesellschaftsrechtlichen Vorgänge erfolgen ausschließlich durch Notare und externe, eigenverantwortliche Partner.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0B1930]">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
            Bereit für Ihre GmbH-Gründung?
          </h2>
          <p className="text-gray-300 mb-6">
            Kontaktieren Sie uns für eine Erstberatung und lassen Sie uns gemeinsam starten.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/onboarding">
              <Button className="bg-white text-[#0B1930] hover:bg-[#E9F7F6] rounded-lg text-sm px-6 py-2.5">
                Formular starten
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button variant="outline" className="border-gray-500 text-gray-300 hover:bg-gray-800 rounded-lg text-sm px-6 py-2.5">
                Beratung anfragen
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default GmbhGruenden;
